/**
 * Centralized subscription plan configuration.
 *
 * This is the SINGLE SOURCE OF TRUTH for all subscription-based features and limits.
 * When changing who gets what:
 *   - Edit this file only
 *   - Components use the SubscriptionPlanService, which reads from here
 *
 * To add a new subscription level:
 *   - Add a new entry below with the same shape as PlanFeatures
 *   - Add the plan key to the SubscriptionPlan type in types.ts
 *   - Ensure database/backend supports the new plan value
 */

/** -1 means unlimited */
export const UNLIMITED = -1;

/** Feature flags - which capabilities each plan has */
export interface PlanFeatureFlags {
  /** Can access the Templates section on Dashboard (admins always see it; others may via env prefs) */
  canOverrideEnvPrefsToViewTemplates: boolean;
  /** Can create new templates (Add Template button in ProjectDetail) */
  canCreateTemplates: boolean;
  /** When opening a template: edit template directly vs "My Templates" flow */
  canEditTemplatesDirectly: boolean;
  /** Can edit template metadata (category, tags, difficulty) when editing a template scene */
  canEditTemplateMetadata: boolean;
  /** Can copy projects (Copy Project button) */
  canCopyProject: boolean;
  /** Can edit scenes in pending/live status (bypass block dialog in editor) */
  canEditPendingLiveScenes: boolean;
  /** Can edit template project name (non-admins see read-only in ProjectEdit) */
  canEditTemplateProjectName: boolean;
  /** Can navigate freely from template scenes (non-admins get redirected to dashboard) */
  canNavigateFreelyFromTemplate: boolean;
  /** Can access the Video Library (upload video assets). Available to all plans. */
  canAccessVideoLibrary: boolean;
  /** Can use Text-to-Video generation (the AI generate flow inside the Video
   *  Library). Superuser/admin only, independent of `canAccessVideoLibrary`
   *  (video upload is open to all plans, generation is not). */
  canGenerateVideo: boolean;
  /** Can access 3D Primitives Library entry point in the side menu. Includes
   *  the Text tab for everyone; Shapes are gated separately via
   *  `canAccessPrimitives3DShapes`. Also allowed when org member
   *  `track_one_member = true`. */
  canAccessPrimitives3DLibrary: boolean;
  /** Can access the Shape tab inside the 3D Primitives Library. Available to
   *  all plans alongside Text. Superuser-only shapes (e.g. particle systems
   *  like snow) are filtered separately via `FormDefinition.superuserOnly`. */
  canAccessPrimitives3DShapes: boolean;
  /** Can select the Web (appless) platform when publishing or sharing. Available
   *  to all plans; the no-install Web experience currently targets iOS only. */
  canUseApplessPlatform: boolean;
}

/** Limits - numeric caps per plan */
export interface PlanLimits {
  maxStories: number;
  maxPublishedStories: number;
  maxFileUploadSizeMB: number;
  maxSceneSizeMB: number;
}

/** Full plan features = limits + flags + derived fields */
export interface PlanFeaturesConfig extends PlanLimits, PlanFeatureFlags {
  versionHistoryEnabled: boolean;
}

/** Plan configuration - add new plans by adding a new key */
export const PLAN_CONFIGURATION: Record<string, PlanFeaturesConfig> = {
  starter: {
    // Limits
    maxStories: 2,
    maxPublishedStories: 0,
    maxFileUploadSizeMB: 10,
    maxSceneSizeMB: 100,
    // Feature flags
    versionHistoryEnabled: false,
    canOverrideEnvPrefsToViewTemplates: false,
    canCreateTemplates: false,
    canEditTemplatesDirectly: false,
    canEditTemplateMetadata: false,
    canCopyProject: false,
    canEditPendingLiveScenes: false,
    canEditTemplateProjectName: false,
    canNavigateFreelyFromTemplate: false,
    canAccessVideoLibrary: true,
    canAccessPrimitives3DLibrary: true,
    canAccessPrimitives3DShapes: true,
    canUseApplessPlatform: true,
    canGenerateVideo: false
  },
  creator: {
    maxStories: UNLIMITED,
    maxPublishedStories: UNLIMITED,
    maxFileUploadSizeMB: 50,
    maxSceneSizeMB: 300,
    versionHistoryEnabled: true,
    canOverrideEnvPrefsToViewTemplates: false,
    canCreateTemplates: false,
    canEditTemplatesDirectly: false,
    canEditTemplateMetadata: false,
    canCopyProject: false,
    canEditPendingLiveScenes: false,
    canEditTemplateProjectName: false,
    canNavigateFreelyFromTemplate: false,
    canAccessVideoLibrary: true,
    canAccessPrimitives3DLibrary: true,
    canAccessPrimitives3DShapes: true,
    canUseApplessPlatform: true,
    canGenerateVideo: false
  },
  pro: {
    maxStories: UNLIMITED,
    maxPublishedStories: UNLIMITED,
    maxFileUploadSizeMB: 100,
    maxSceneSizeMB: 500,
    versionHistoryEnabled: true,
    canOverrideEnvPrefsToViewTemplates: false,
    canCreateTemplates: false,
    canEditTemplatesDirectly: false,
    canEditTemplateMetadata: false,
    canCopyProject: false,
    canEditPendingLiveScenes: false,
    canEditTemplateProjectName: false,
    canNavigateFreelyFromTemplate: false,
    canAccessVideoLibrary: true,
    canAccessPrimitives3DLibrary: true,
    canAccessPrimitives3DShapes: true,
    canUseApplessPlatform: true,
    canGenerateVideo: false
  },
  superuser: {
    maxStories: UNLIMITED,
    maxPublishedStories: UNLIMITED,
    maxFileUploadSizeMB: 100,
    maxSceneSizeMB: 500,
    versionHistoryEnabled: true,
    canOverrideEnvPrefsToViewTemplates: false,
    canCreateTemplates: false,
    canEditTemplatesDirectly: false,
    canEditTemplateMetadata: false,
    canCopyProject: true,
    canEditPendingLiveScenes: true,
    canEditTemplateProjectName: false,
    canNavigateFreelyFromTemplate: false,
    canAccessVideoLibrary: true,
    canAccessPrimitives3DLibrary: true,
    canAccessPrimitives3DShapes: true,
    canUseApplessPlatform: true,
    canGenerateVideo: true
  },
  admin: {
    maxStories: UNLIMITED,
    maxPublishedStories: UNLIMITED,
    maxFileUploadSizeMB: 100,
    maxSceneSizeMB: 500,
    versionHistoryEnabled: true,
    canOverrideEnvPrefsToViewTemplates: true,
    canCreateTemplates: true,
    canEditTemplatesDirectly: true,
    canEditTemplateMetadata: true,
    canCopyProject: true,
    canEditPendingLiveScenes: true,
    canEditTemplateProjectName: true,
    canNavigateFreelyFromTemplate: true,
    canAccessVideoLibrary: true,
    canAccessPrimitives3DLibrary: true,
    canAccessPrimitives3DShapes: true,
    canUseApplessPlatform: true,
    canGenerateVideo: true
  }
};

/** Default plan when unknown/invalid plan string is passed */
export const DEFAULT_PLAN = 'creator';

/** All valid plan keys - derived from config for type safety */
export const VALID_PLANS = Object.keys(PLAN_CONFIGURATION) as readonly string[];

/**
 * Check if a string is a valid plan key.
 * Use this when normalizing user input or DB values.
 */
export function isValidPlan(plan: string): plan is keyof typeof PLAN_CONFIGURATION {
  const normalized = (plan || '').toLowerCase().trim();
  return normalized in PLAN_CONFIGURATION;
}

/**
 * Normalize plan string to valid key (lowercase, trimmed).
 * Returns DEFAULT_PLAN for unknown plans.
 */
export function normalizePlan(plan: string): keyof typeof PLAN_CONFIGURATION {
  const normalized = (plan || '').toLowerCase().trim();
  if (normalized in PLAN_CONFIGURATION) {
    return normalized as keyof typeof PLAN_CONFIGURATION;
  }
  if (normalized !== '') {
    console.warn(`[subscription-plan] Unknown subscription plan: ${JSON.stringify(plan)}. Defaulting to ${DEFAULT_PLAN}`);
  }
  return DEFAULT_PLAN as keyof typeof PLAN_CONFIGURATION;
}
